import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Lock, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TRANSLATIONS = {
  en: {
    title: "Reset Password",
    subtitle: "Create a new password for your account",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    passwordPlaceholder: "Enter new password",
    confirmPlaceholder: "Confirm new password",
    submit: "Reset Password",
    resetting: "Resetting...",
    back: "Back to Login",
    successTitle: "Password Reset!",
    successMessage: "Your password has been reset successfully. You can now login with your new password.",
    loginNow: "Login Now",
    errorTitle: "Invalid Link",
    errorMessage: "This password reset link is invalid or has expired.",
    requestNew: "Request New Link",
    passwordMismatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 6 characters"
  },
  ms: {
    title: "Tetapkan Semula Kata Laluan",
    subtitle: "Buat kata laluan baru untuk akaun anda",
    newPassword: "Kata Laluan Baru",
    confirmPassword: "Sahkan Kata Laluan",
    passwordPlaceholder: "Masukkan kata laluan baru",
    confirmPlaceholder: "Sahkan kata laluan baru",
    submit: "Tetapkan Semula",
    resetting: "Menetapkan semula...",
    back: "Kembali ke Log Masuk",
    successTitle: "Kata Laluan Ditetapkan Semula!",
    successMessage: "Kata laluan anda telah berjaya ditetapkan semula. Anda boleh log masuk dengan kata laluan baru.",
    loginNow: "Log Masuk Sekarang",
    errorTitle: "Pautan Tidak Sah",
    errorMessage: "Pautan tetapan semula kata laluan ini tidak sah atau telah tamat tempoh.",
    requestNew: "Minta Pautan Baru",
    passwordMismatch: "Kata laluan tidak sepadan",
    passwordTooShort: "Kata laluan mesti sekurang-kurangnya 6 aksara"
  }
};

function getSavedLanguage() {
  return localStorage.getItem('myshield_lang') || 'en';
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  
  const lang = getSavedLanguage();
  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  useEffect(() => {
    if (!token || !email) {
      setError(true);
    }
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error(t.passwordTooShort);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(t.passwordMismatch);
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        email,
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
      if (err.response?.status === 400) {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-[#0A1128] to-black p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>
      
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/login")}
        className="absolute top-4 left-4 gap-2 z-10 text-white/60 hover:text-white hover:bg-white/10"
        data-testid="back-to-login-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {t.back}
      </Button>
      
      <div className="w-full max-w-md glass-card p-8 relative z-10 animate-fade-in" data-testid="reset-password-card">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_protect-workers/artifacts/awekt278_image.png" 
            alt="MyShield Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          
          {error ? (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white">{t.errorTitle}</h1>
              <p className="text-white/50 text-sm mt-2">{t.errorMessage}</p>
            </>
          ) : success ? (
            <>
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold text-white">{t.successTitle}</h1>
              <p className="text-white/50 text-sm mt-2">{t.successMessage}</p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-white">{t.title}</h1>
              <p className="text-white/50 text-sm mt-2">{t.subtitle}</p>
            </>
          )}
        </div>

        {error ? (
          <Link to="/forgot-password">
            <Button
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 rounded-xl transition-all"
              data-testid="request-new-link-button"
            >
              {t.requestNew}
            </Button>
          </Link>
        ) : success ? (
          <Link to="/login">
            <Button
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 rounded-xl transition-all"
              data-testid="login-now-button"
            >
              {t.loginNow}
            </Button>
          </Link>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-white/70">{t.newPassword}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t.passwordPlaceholder}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="new-password-input"
                  className="h-12 pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/50"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">{t.confirmPassword}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t.confirmPlaceholder}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                  className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 rounded-xl transition-all active:scale-[0.98]"
              disabled={loading}
              data-testid="reset-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t.resetting}
                </>
              ) : (
                t.submit
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
